import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import OptimizedHeroImage from "@/components/OptimizedHeroImage";
import heroBackgroundWebp from "@/assets/hero-depin-africa-new.webp";
const Hero = () => {
  const {
    ref: sectionRef,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const [scrollY, setScrollY] = useState(0);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Calculate parallax transform (slower scroll for background)
  const parallaxOffset = scrollY * 0.3;
  
  // Calculate fade and scale based on scroll for smooth hero effect
  const heroOpacity = Math.max(0, 1 - scrollY / 600);
  const heroScale = Math.max(0.95, 1 - scrollY / 3000);
  const heroBlur = Math.min(scrollY / 100, 5);
  
  return <section ref={sectionRef as React.RefObject<HTMLElement>} className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-background pt-20 pb-8 md:pb-0">
      {/* Optimized Background Image with parallax and fade effect */}
      <div className="absolute inset-0 z-0 transition-[filter] duration-100" style={{
      transform: `translateY(${parallaxOffset}px) scale(${1.1 * heroScale})`,
      opacity: heroOpacity,
      filter: `blur(${heroBlur}px)`
    }}>
        <OptimizedHeroImage src={heroBackgroundWebp} webpSrc={heroBackgroundWebp} alt="DePIN Land hero background" className="z-0" priority={true} />
      </div>

      {/* Dark overlay for text readability */}
      {/* Dark overlay for text readability - reduced */}
      <div className="absolute inset-0 z-[1] bg-black/40" />

      {/* Static gradient overlay - no animation for power saving */}
      <div className="absolute inset-0 z-[2] bg-gradient-to-br from-black/40 via-transparent to-black/30" />
      
      {/* Static gradient orbs - hidden on mobile for power saving */}
      <div className="absolute inset-0 z-[2] overflow-hidden pointer-events-none hidden md:block">
        <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-white/5 rounded-full blur-[80px]" />
        <div className="absolute bottom-1/3 right-1/4 w-[300px] h-[300px] bg-white/3 rounded-full blur-[60px]" />
      </div>

      {/* Cinematic vignette overlay */}
      <div className="absolute inset-0 z-[3] bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.4)_70%,rgba(0,0,0,0.7)_100%)]" />
      
      {/* Gradient overlay from left */}
      <div className="absolute inset-0 z-[4] bg-gradient-to-r from-background/70 via-background/30 to-transparent" />

      {/* Bottom fade */}
      <div className="absolute inset-0 z-[5] bg-gradient-to-t from-background/80 via-transparent to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-40 z-[6] bg-gradient-to-t from-background to-transparent" />
      
      {/* Top subtle vignette */}
      <div className="absolute inset-0 z-[4] bg-gradient-to-b from-background/40 via-transparent to-transparent" />

      {/* Animated scan line effect */}
      <div className="absolute inset-0 z-[6] pointer-events-none overflow-hidden opacity-[0.03]">
        <div className="absolute inset-0 bg-[repeating-linear-gradient(0deg,transparent,transparent_2px,rgba(255,255,255,0.1)_2px,rgba(255,255,255,0.1)_4px)]" />
      </div>

      {/* Main Content - Centered with scroll fade effect */}
      <div 
        className="container mx-auto px-4 sm:px-6 relative z-10 py-6 md:py-20 flex flex-col items-center text-center transition-all duration-100"
        style={{
          opacity: heroOpacity,
          transform: `translateY(${scrollY * 0.1}px) scale(${heroScale})`
        }}
      >
        <div className="max-w-4xl w-full">
          {/* Floating badge */}
          

          {/* Main Headline with staggered animation */}
          <div className={`transition-all duration-1000 ease-out ${mounted && isVisible ? "translate-y-0 opacity-100 blur-0" : "translate-y-12 opacity-0 blur-sm"}`} style={{
          transitionDelay: '200ms'
        }}>
            <h1 className="text-4xl md:text-6xl lg:text-8xl font-bold text-foreground mb-6 md:mb-8 leading-tight drop-shadow-[0_4px_20px_rgba(0,0,0,0.8)]">
              Empowering
              <br />
              Africa Through
              <br />
              <span className="bg-gradient-to-r from-foreground via-foreground/80 to-foreground/60 bg-clip-text text-transparent">DePIN</span>
            </h1>
          </div>

          {/* Description with fade-in */}
          <div className={`transition-all duration-1000 ease-out ${mounted && isVisible ? "translate-y-0 opacity-100 blur-0" : "translate-y-12 opacity-0 blur-sm"}`} style={{
          transitionDelay: '400ms'
        }}>
            <p className="text-base sm:text-lg md:text-xl leading-relaxed mb-8 md:mb-10 max-w-2xl mx-auto px-2 drop-shadow-[0_4px_20px_rgba(0,0,0,0.8)] text-muted-foreground">
              Building bridges across cultures and communities from Infrastructure gaps to shared Ownership and Opportunities.
            </p>
          </div>

          {/* CTA Buttons with mega styling */}
          <div className={`transition-all duration-1000 ease-out ${mounted && isVisible ? "translate-y-0 opacity-100 blur-0" : "translate-y-12 opacity-0 blur-sm"}`} style={{
          transitionDelay: '600ms'
        }}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="group relative gap-2 px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg font-semibold bg-gradient-to-r from-primary to-lime-500 hover:from-lime-500 hover:to-primary text-primary-foreground shadow-[0_0_30px_rgba(163,230,53,0.4)] hover:shadow-[0_0_50px_rgba(163,230,53,0.6)] transition-all duration-500 border-0 overflow-hidden">
                <Link to="/ventures">
                  <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
                  <span className="relative z-10 flex items-center gap-2">
                    Our Ventures 
                    <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="group px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg font-semibold bg-background/20 backdrop-blur-sm border-2 border-primary/50 hover:border-primary hover:bg-primary/10 text-foreground transition-all duration-300">
                <Link to="/ecosystem">
                  <span className="group-hover:text-primary transition-colors">Ecosystem</span>
                </Link>
              </Button>
            </div>
          </div>

          {/* Floating stats */}
          <div className={`transition-all duration-1000 ease-out ${mounted && isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`} style={{
          transitionDelay: '1000ms'
        }}>
            
          </div>
        </div>
      </div>

      {/* Scroll Indicator with animation */}
      
    </section>;
};
export default Hero;